import React from 'react';
import { useEffect, useState } from 'react';
import apiHelper from '../../utils/apiHelpers';
import Loader from '../common/Loader';
import StoreTotalsComponent from './StoreTotals';
import formatter from '../../utils/formatter';
import Alert from '../common/Alert';
import StoreProgressComponent from './StoreProgress';

const Stats = () => {
  const [stats, setStats] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getStats();
    // Set up an interval to call the API every 1 minute
    const interval = setInterval(() => {
      getStats();
    }, 60000); // 60000 milliseconds = 1 minute
    // Cleanup the interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const getStats = () => {
    apiHelper
      .getStats()
      .then(response => {
        setErrorMessage('');
        setStats(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.log(error);
        setErrorMessage('Error obteniendo métricas');
      });
  };

  return (
    <div className="p-4">
      {loading ? (
        <Loader />
      ) : (
        <div className="flex flex-col gap-y-1">
          <Alert>{errorMessage}</Alert>
          {/* Manual bulk imports report here because they run for minutes with no other
              feedback — the same reason the store crawlers report isCrawling below. */}
          {stats && stats.importJobs && stats.importJobs.length > 0 && (
            <>
              <h1 className="text-white text-2xl font-bold">
                Importaciones manuales
              </h1>
              {stats.importJobs.map(job => (
                <h2 key={job.name} className="text-white text-lg font-semibold">
                  {job.label}:{' '}
                  {job.running ? (
                    <>
                      En ejecución{' '}
                      <i className="fas fa-clock text-yellow-500" /> (desde{' '}
                      {formatter.formatDateTime(job.startedAt)})
                    </>
                  ) : job.lastError ? (
                    <>
                      Falló <i className="fas fa-times text-red-500" /> (
                      {job.lastError})
                    </>
                  ) : (
                    <>
                      Finalizada <i className="fas fa-check text-green-500" /> (
                      {formatter.formatDateTime(job.finishedAt)})
                    </>
                  )}
                </h2>
              ))}
            </>
          )}
          <h1 className="text-white text-2xl font-bold">Crawlers</h1>
          {stats &&
            stats.crawlerStatus.map(store => (
              <h2
                key={store.store}
                className="text-white text-lg font-semibold">
                {store.store}:{' '}
                {store.isCrawling ? 'En ejecución' : 'Finalizado'}{' '}
                {store.isCrawling ? (
                  <i className="fas fa-clock text-yellow-500" />
                ) : (
                  <i className="fas fa-check text-green-500" />
                )}{' '}
                (Último precio actualizado:{' '}
                {formatter.formatDateTime(
                  stats.mostRecentPriceUpdatedPerStore.find(
                    recentPrice => recentPrice.card_price_store === store.store,
                  )?.mostRecentUpdatedAt,
                )}
                )
              </h2>
            ))}
          <h1 className="text-white text-2xl font-bold">Usuarios</h1>
          <h2 className="text-white text-lg font-semibold">
            Total de usuarios: {formatter.formatNumber(stats.totalUsers)}
          </h2>
          <h1 className="text-white text-2xl font-bold">Cartas</h1>
          <h2 className="text-white text-lg font-semibold">
            Total de cartas: {formatter.formatNumber(stats.totalCards)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Total de cartas creadas en las últimas 24hs:{' '}
            {formatter.formatNumber(stats.cardsCreated24hs)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Total de cartas actualizadas en las últimas 24hs:{' '}
            {formatter.formatNumber(stats.cardsUpdated24hs)}
          </h2>
          <h1 className="text-white text-2xl font-bold">Precios</h1>
          <h2 className="text-white text-lg font-semibold">
            Total de precios: {formatter.formatNumber(stats.totalCardPrices)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Total de precios de cartas creados en las últimas 24hs:{' '}
            {formatter.formatNumber(stats.cardPricesCreated24hs)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Total de precios de cartas actualizados en las últimas 24hs:{' '}
            {formatter.formatNumber(stats.cardPricesUpdated24hs)}
          </h2>
          <h1 className="text-white text-2xl font-bold">
            Scryfall (importación masiva)
          </h1>
          <h2 className="text-white text-lg font-semibold">
            Cartas con datos de Scryfall (URL imagen):{' '}
            {formatter.formatNumber(stats.cardWithScryfallData)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Cartas sin datos de Scryfall (URL imagen):{' '}
            {formatter.formatNumber(stats.cardWithoutScryfallData)}
          </h2>
          {/* Coverage of the bulk import. If "sincronizadas" stays near zero the
              import is not landing, and card lookups fall back to the live API. */}
          <h2 className="text-white text-lg font-semibold">
            Cartas sincronizadas desde el bulk de Scryfall:{' '}
            {formatter.formatNumber(stats.cardsSyncedFromScryfall)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Cartas nunca sincronizadas (creadas por los crawlers):{' '}
            {formatter.formatNumber(stats.cardsNeverSyncedFromScryfall)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Última sincronización:{' '}
            {stats.lastScryfallSync
              ? new Date(stats.lastScryfallSync).toLocaleString('es-AR')
              : 'nunca'}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Rulings importados: {formatter.formatNumber(stats.totalRulings)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Nombres sin resolver (no se reintentan por ahora):{' '}
            {formatter.formatNumber(stats.negativeCachedLookups)}
          </h2>
          {/* Tag-import health. Card search is answered from these tables, so an
              empty card_tag means the functional-tag filter silently matches nothing. */}
          <h2 className="text-white text-lg font-semibold">
            Etiquetas funcionales importadas:{' '}
            {formatter.formatNumber(stats.totalTags)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Asociaciones carta-etiqueta:{' '}
            {formatter.formatNumber(stats.totalTagAssociations)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Cartas con rol asignado:{' '}
            {formatter.formatNumber(stats.totalCardRoles)}
          </h2>
          {/* Rules and combos back the chatbot's grounded answers: if these are empty
              it silently loses the ability to cite anything. */}
          <h1 className="text-white text-2xl font-bold">
            Reglas y combos (importación masiva)
          </h1>
          <h2 className="text-white text-lg font-semibold">
            Reglas Comprehensivas importadas:{' '}
            {formatter.formatNumber(stats.totalRules)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Términos del glosario:{' '}
            {formatter.formatNumber(stats.totalGlossaryTerms)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Reglas vigentes desde: {stats.rulesEffectiveDate || 'sin importar'}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Combos importados: {formatter.formatNumber(stats.totalCombos)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Piezas de combo: {formatter.formatNumber(stats.totalComboCards)}
          </h2>
          <h1 className="text-white text-2xl font-bold">
            TCGPlayer (precio de referencia secundario, sólo por cron)
          </h1>
          <h2 className="text-white text-lg font-semibold">
            Cartas con precio estándar de TCGPlayer:{' '}
            {formatter.formatNumber(stats.cardWithScryfallPrice)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Cartas sin precio estándar de TCGPlayer:{' '}
            {formatter.formatNumber(stats.cardWithoutScryfallPrice)}
          </h2>
          <h1 className="text-white text-2xl font-bold">CardKingdom</h1>
          <h2 className="text-white text-lg font-semibold">
            Cartas con precio estándar de CardKingdom:{' '}
            {formatter.formatNumber(stats.cardWithCardKingdomPrice)}
          </h2>
          <h2 className="text-white text-lg font-semibold">
            Cartas sin precio estándar de CardKingdom:{' '}
            {formatter.formatNumber(stats.cardWithoutCardKingdomPrice)}
          </h2>
          <h1 className="text-white text-2xl font-bold">
            Precios registrados por store
          </h1>
          <StoreTotalsComponent
            totalPricesPerStore={stats.totalPricesPerStore}
          />
          <h1 className="text-white text-2xl font-bold">
            Precios actualizados hoy por store
          </h1>
          <StoreTotalsComponent
            totalPricesPerStore={stats.updatedPricesPerStore}
          />
          <h1 className="text-white text-2xl font-bold">
            Progreso de actualización por store
          </h1>
          <StoreProgressComponent
            totalPricesPerStore={stats.totalPricesPerStore}
            updatedPricesPerStore={stats.updatedPricesPerStore}
          />
        </div>
      )}
    </div>
  );
};

export default Stats;
